import React, { useState, useEffect } from 'react';
import { SEO } from './SEO';
import '../styles/skillplanner.css';
import { GemCard } from './GemCard';
import { GemSelector } from './GemSelector';
import { BuildManager } from './BuildManager';

interface Gem {
  name: string;
  type: string;
  tags: string;
  description: string;
  effects: string[];
}

interface Build {
  id: string;
  name: string;
  skillSockets: (Gem | null)[];
  supportSockets: (Gem | null)[][];
  isExpanded: boolean[];
}

const encodeData = (data: any) => {
  // Convert to JSON and encode to base64, handling Unicode characters
  return btoa(unescape(encodeURIComponent(JSON.stringify(data))));
};

const decodeData = (str: string) => {
  // Decode base64 and parse JSON, handling Unicode characters
  return JSON.parse(decodeURIComponent(escape(atob(str))));
};

export const PoE2SkillsPlanner: React.FC = () => {
  const [builds, setBuilds] = useState<Build[]>([]);
  const [currentBuildId, setCurrentBuildId] = useState<string>('');
  const [hoveredSocket, setHoveredSocket] = useState<{row: number, col: number} | null>(null);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectedSocket, setSelectedSocket] = useState<{row: number, col: number | null} | null>(null);
  
  const skills = require('../data/all_skills.json') as Gem[];
  const supports = require('../data/all_supports.json') as Gem[];

  // Load builds from localStorage
  useEffect(() => {
    const savedBuilds = localStorage.getItem('poe2-builds');
    if (savedBuilds) {
      setBuilds(JSON.parse(savedBuilds));
    }
  }, []);

  // Save builds to localStorage
  useEffect(() => {
    if (builds.length > 0) {
      localStorage.setItem('poe2-builds', JSON.stringify(builds));
    }
  }, [builds]);

  const createNewBuild = () => {
    const newBuild: Build = {
      id: Date.now().toString(),
      name: 'New Build',
      skillSockets: Array(9).fill(null),
      supportSockets: Array(9).fill(null).map(() => Array(5).fill(null)),
      isExpanded: Array(9).fill(false)
    };
    setBuilds([...builds, newBuild]);
    setCurrentBuildId(newBuild.id);
  };

  const currentBuild = builds.find(b => b.id === currentBuildId);

  const handleSocketClick = (row: number, col: number | null) => {
    setSelectedSocket({ row, col });
    setSelectorOpen(true);
  };

  const handleGemSelect = (gem: Gem) => {
    if (!selectedSocket || !currentBuild) return;
    
    const newBuilds = builds.map(build => {
      if (build.id === currentBuildId) {
        if (selectedSocket.col === null) {
          // Skill gem socket
          const newSkillSockets = [...build.skillSockets];
          newSkillSockets[selectedSocket.row] = gem;
          return { ...build, skillSockets: newSkillSockets };
        } else {
          // Support gem socket
          const newSupportSockets = [...build.supportSockets];
          
          // Check if this support gem exists in any socket across all skills
          for (let skillRow = 0; skillRow < newSupportSockets.length; skillRow++) {
            const existingSocketIndex = newSupportSockets[skillRow]
              .findIndex(supportGem => supportGem?.name === gem.name);
            
            if (existingSocketIndex !== -1) {
              // Remove from old socket
              newSupportSockets[skillRow][existingSocketIndex] = null;
            }
          }
          
          // Add to new socket
          newSupportSockets[selectedSocket.row][selectedSocket.col] = gem;
          
          return { ...build, supportSockets: newSupportSockets };
        }
      }
      return build;
    });
    
    setBuilds(newBuilds);
    setSelectorOpen(false);
    setSelectedSocket(null);
  };

  const toggleExpand = (row: number) => {
    if (!currentBuild) return;
    
    const newBuilds = builds.map(build => {
      if (build.id === currentBuildId) {
        const newIsExpanded = Array(9).fill(false);
        newIsExpanded[row] = !build.isExpanded[row];
        return { ...build, isExpanded: newIsExpanded };
      }
      return build;
    });
    
    setBuilds(newBuilds);
  };

  const handleRenameBuild = (id: string, newName: string) => {
    const newBuilds = builds.map(build => 
      build.id === id ? { ...build, name: newName } : build
    );
    setBuilds(newBuilds);
  };

  const handleDeleteBuild = (id: string) => {
    if (window.confirm('Are you sure you want to delete this build?')) {
      const newBuilds = builds.filter(build => build.id !== id);
      setBuilds(newBuilds);
      setCurrentBuildId(newBuilds[0]?.id || '');
    }
  };

  // Load build from URL if present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const buildData = params.get('build');
    
    if (buildData) {
      try {
        const decodedData = decodeData(buildData);
        const newBuild: Build = {
          id: Date.now().toString(),
          name: decodedData.name,
          skillSockets: decodedData.skillSockets,
          supportSockets: decodedData.supportSockets,
          isExpanded: Array(9).fill(false)
        };
        setBuilds(prevBuilds => [...prevBuilds, newBuild]);
        setCurrentBuildId(newBuild.id);
      } catch (error) {
        console.error('Failed to load build from URL:', error);
      }
    }
  }, []);

  const getGemImage = (gem: Gem) => {
    if (gem.type.toLowerCase().includes('support')) {
      return '/images/gems/support_gem.webp';
    } else if (gem.type.toLowerCase().includes('spirit')) {
      return '/images/gems/spirit_gem.webp';
    }
    return '/images/gems/skill_gem.webp';
  };

  return (
    <>
      <SEO 
        title="POE2 Skill Planner"
        description="Plan your Path of Exile 2 skill gem combinations and build."
        canonicalUrl="/poe2skillplanner"
      />
      <div className="planner-container">
        <BuildManager
          currentBuild={currentBuild}
          builds={builds}
          onCreateBuild={createNewBuild}
          onSelectBuild={setCurrentBuildId}
          onRenameBuild={handleRenameBuild}
          onDeleteBuild={handleDeleteBuild}
          onShareBuild={(build: Build) => {
            const buildData = {
              name: build.name,
              skillSockets: build.skillSockets,
              supportSockets: build.supportSockets,
            };
            try {
              const encodedData = encodeData(buildData);
              const shareUrl = `${window.location.origin}/poe2skillplanner?build=${encodedData}`;
              navigator.clipboard.writeText(shareUrl)
                .then(() => alert('Build link copied to clipboard!'))
                .catch(() => {
                  const textarea = document.createElement('textarea');
                  textarea.value = shareUrl;
                  document.body.appendChild(textarea);
                  textarea.select();
                  document.execCommand('copy');
                  document.body.removeChild(textarea);
                  alert('Build link copied to clipboard!');
                });
            } catch (error) {
              console.error('Failed to share build:', error);
              alert('Failed to generate share link. Please try again.');
            }
          }}
        />
        
        {currentBuild && (
          <div className="socket-grid">
            {currentBuild.skillSockets.map((skillGem, row) => (
              <div key={row} className="socket-row">
                <div 
                  className="skill-socket"
                  onClick={() => handleSocketClick(row, null)}
                >
                  {skillGem ? (
                    <div className="socket-content">
                      <img 
                        src={getGemImage(skillGem)}
                        alt={skillGem.name}
                        className="gem-image"
                      />
                    </div>
                  ) : (
                    <div className="empty-socket">Empty</div>
                  )}
                </div>
                
                {skillGem && (
                  <>
                    <GemCard 
                      gem={skillGem}
                      isExpanded={currentBuild.isExpanded[row]}
                      onToggleExpand={() => toggleExpand(row)}
                    />
                    <div className="support-sockets">
                      {currentBuild.supportSockets[row].map((supportGem, col) => (
                        <div 
                          key={col} 
                          className="support-socket"
                          onClick={() => handleSocketClick(row, col)}
                        >
                          {supportGem ? (
                            <div className="socket-content">
                              <img 
                                src={getGemImage(supportGem)}
                                alt={supportGem.name}
                                className="gem-image"
                              />
                            </div>
                          ) : (
                            <div className="empty-socket">Empty</div>
                          )}
                        </div>
                      ))}
                    </div>
                    {currentBuild.supportSockets[row].some(gem => gem) ? (
                      <div className="support-gems">
                        {currentBuild.supportSockets[row].map((supportGem, col) => (
                          supportGem && (
                            <GemCard 
                              key={col}
                              gem={supportGem}
                              isExpanded={currentBuild.isExpanded[row]}
                              onToggleExpand={() => toggleExpand(row)}
                            />
                          )
                        ))}
                      </div>
                    ) : (
                      <div className="support-empty">No support gems inserted</div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      <GemSelector
        isOpen={selectorOpen}
        onClose={() => {
          setSelectorOpen(false);
          setSelectedSocket(null);
        }}
        onSelect={handleGemSelect}
        gems={selectedSocket?.col === null ? skills : supports}
        type={selectedSocket?.col === null ? 'skill' : 'support'}
      />
    </>
  );
}; 